import React, { useState, useCallback, useMemo } from 'react';
import '@/App.css';
import { Header } from '@/components/Header';
import { Sidebar } from '@/components/Sidebar';
import { ContentView } from '@/components/ContentView';
import { QuizSection } from '@/components/QuizSection';

import { SearchDialog } from '@/components/SearchDialog';
import { LandingPage, hasStarted } from '@/components/LandingPage';
import { HomePage } from '@/components/HomePage';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useProgress } from '@/hooks/useProgress';
import { useBookmarks } from '@/hooks/useBookmarks';
import { useTheme } from '@/hooks/useTheme';
import { courseModules, getAllSections } from '@/data/courseData';
import { getLMSStudentName } from '@/lib/scorm';

// Pages: 'landing' → 'home' → 'course'
function App() {
  const [userName, setUserName] = useState(() => {
    try {
      const lmsName = getLMSStudentName();
      if (lmsName) {
        localStorage.setItem('sn-elearning-student-name', lmsName);
        return lmsName;
      }
      return localStorage.getItem('sn-elearning-student-name') || '';
    } catch { return ''; }
  });

  const [page, setPage] = useState(() => {
    try {
      const lmsName = getLMSStudentName();
      if (lmsName) {
        localStorage.setItem('sn-elearning-started', 'true');
        return 'home';
      }
    } catch {}
    return hasStarted() ? 'home' : 'landing';
  });
  const allSections = useMemo(() => getAllSections(), []);

  const totalCompletable = useMemo(() => {
    let count = 0;
    courseModules.forEach(mod => {
      mod.topics.forEach(topic => {
        if (topic.subtopics) count += topic.subtopics.length;
        if (topic.quiz) count += 1;
      });
      if (mod.quiz) count += 1;
    });
    return count;
  }, []);

  const {
    completedSections, currentSection,
    markComplete, saveQuizScore, setCurrentSection,
    isCompleted, getQuizScore, completionPercent, isAllComplete, resetProgress
  } = useProgress(totalCompletable, userName);

  const { bookmarks, toggleBookmark, isBookmarked } = useBookmarks(userName);
  const { isDark, toggleTheme } = useTheme();

  const [searchOpen, setSearchOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  React.useEffect(() => {
    if (isAllComplete) {
      window.parent.postMessage({ type: "COURSE_COMPLETED" }, "*");
    }
  }, [isAllComplete]);

  React.useEffect(() => {
    if (page !== 'course') return;
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [page]);

  const orderedSections = useMemo(() => {
    const arr = [];
    courseModules.forEach(mod => {
      mod.topics.forEach(topic => {
        if (topic.subtopics) {
          topic.subtopics.forEach(sub => arr.push({ ...sub, moduleId: mod.id, moduleName: mod.title, topicName: topic.title }));
        }
        if (topic.quiz) arr.push({ ...topic.quiz, type: 'quiz', moduleId: mod.id, moduleName: mod.title, topicName: topic.title });
      });
      if (mod.quiz) arr.push({ ...mod.quiz, type: 'quiz', moduleId: mod.id, moduleName: mod.title });
    });
    return arr;
  }, []);

  const currentIndex = useMemo(() => orderedSections.findIndex(s => s.id === currentSection), [orderedSections, currentSection]);
  const activeData = useMemo(() => orderedSections[currentIndex] || null, [orderedSections, currentIndex]);

  const handleSelect = useCallback((sectionId) => {
    if (!sectionId) return;
    setCurrentSection(sectionId);
    setSidebarOpen(false);
    window.scrollTo(0, 0);
  }, [setCurrentSection]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) { setCurrentSection(orderedSections[currentIndex - 1].id); window.scrollTo(0, 0); }
  }, [currentIndex, orderedSections, setCurrentSection]);

  const handleNext = useCallback(() => {
    if (currentIndex < orderedSections.length - 1) { setCurrentSection(orderedSections[currentIndex + 1].id); window.scrollTo(0, 0); }
  }, [currentIndex, orderedSections, setCurrentSection]);

  const handleQuizComplete = useCallback((quizId, score) => {
    saveQuizScore(quizId, score);
    markComplete(quizId);
  }, [saveQuizScore, markComplete]);

  const findQuiz = useCallback((quizId) => {
    for (const mod of courseModules) {
      if (mod.quiz && mod.quiz.id === quizId) return { quiz: mod.quiz, moduleName: mod.title };
      for (const topic of mod.topics) {
        if (topic.quiz && topic.quiz.id === quizId) return { quiz: topic.quiz, moduleName: `${mod.title} - ${topic.title}` };
      }
    }
    return null;
  }, []);

  const handleSelectModule = useCallback((moduleId) => {
    // Find the first subtopic of the selected module
    const mod = courseModules.find(m => m.id === moduleId);
    if (mod?.topics?.[0]?.subtopics?.[0]) {
      setCurrentSection(mod.topics[0].subtopics[0].id);
    }
    setPage('course');
    window.scrollTo(0, 0);
  }, [setCurrentSection]);

  // Page 1: Landing (name collection)
  if (page === 'landing') {
    return <LandingPage onStart={() => {
      setUserName(localStorage.getItem('sn-elearning-student-name') || '');
      setPage('home');
    }} />;
  }

  // Page 2: Home (module overview)
  if (page === 'home') {
    return (
      <HomePage
        onSelectModule={handleSelectModule}
        completionPercent={completionPercent}
        onResetProgress={resetProgress}
      />
    );
  }

  // Page 3: Course view
  const isQuiz = activeData?.type === 'quiz';
  const quizInfo = isQuiz ? findQuiz(activeData.id) : null;

  const sidebarContent = (
    <Sidebar
      modules={courseModules}
      activeSection={currentSection}
      isCompleted={isCompleted}
      isBookmarked={isBookmarked}
      onSelect={handleSelect}
      completionPercent={completionPercent}
      isAllComplete={isAllComplete}
    />
  );

  return (
    <div className="app-root min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <Header
        isDark={isDark}
        toggleTheme={toggleTheme}
        completionPercent={completionPercent}
        onSearchOpen={() => setSearchOpen(true)}
        onMenuToggle={() => setSidebarOpen(true)}
        onHome={() => { setPage('home'); window.scrollTo(0, 0); }}
      />

      <div className="pt-16 flex h-[calc(100vh-4rem)]">
        <div className="hidden lg:block w-72 shrink-0 h-full overflow-hidden">
          {sidebarContent}
        </div>

        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="p-0 w-80">
            <SheetTitle className="sr-only">Course Navigation</SheetTitle>
            {sidebarContent}
          </SheetContent>
        </Sheet>

        <main className="flex-1 h-full overflow-y-auto border-l border-zinc-200 dark:border-zinc-800">
          {isQuiz && quizInfo ? (
            <QuizSection
              key={activeData.id}
              quiz={quizInfo.quiz}
              onComplete={handleQuizComplete}
              existingScore={getQuizScore(activeData.id)}
              moduleName={quizInfo.moduleName}
            />
          ) : activeData ? (
            <ContentView
              section={activeData}
              isCompleted={isCompleted}
              isBookmarked={isBookmarked}
              onToggleBookmark={toggleBookmark}
              onMarkComplete={markComplete}
              onPrev={handlePrev}
              onNext={handleNext}
              hasPrev={currentIndex > 0}
              hasNext={currentIndex < orderedSections.length - 1}
            />
          ) : (
            <ContentView section={null} isCompleted={isCompleted} isBookmarked={isBookmarked}
              onToggleBookmark={toggleBookmark} onMarkComplete={markComplete}
              onPrev={handlePrev} onNext={handleNext} hasPrev={false} hasNext={false}
            />
          )}
        </main>
      </div>

      <SearchDialog isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        sections={allSections} bookmarks={bookmarks} onSelect={handleSelect} />

    </div>
  );
}

export default App;
